import { useState } from 'react';
import { Calculator, TrendingDown } from 'lucide-react';
import { DeveloperPageLayout } from '@/components/developer/shared/DeveloperPageLayout';
import { TechnicalMetricCard } from '@/components/developer/TechnicalMetricCard';
import { TokenAnalysis } from '@/components/developer/sections/TokenAnalysis';
import { ServiceCostBreakdown } from '@/components/developer/sections/ServiceCostBreakdown';
import { CostOptimization } from '@/components/developer/sections/CostOptimization';
import { CostForecasting } from '@/components/developer/sections/CostForecasting';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

// Mock hook for technical cost data
const useTechnicalCosts = (timeRange: string) => ({
  costs: {
    tokenEfficiency: 68,
    avgApiCallCost: '$0.0023',
    apiCallTrend: -12,
    cacheHitRate: 87,
    wastedTokens: 45231,
    costPerUser: '$2.34',
    totalCost: '$1,247.83',
    totalRequests: 89432,
    totalTokens: 2847291,
    optimizationPotential: '$147.20'
  },
  loading: false,
  error: null
});

const formatNumber = (num: number): string => {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M';
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K';
  }
  return num.toString();
};

export function DeveloperCosts() {
  const [timeRange, setTimeRange] = useState('24h');
  const { costs } = useTechnicalCosts(timeRange);
  
  const openCostCalculator = () => {
    console.log('Opening cost calculator...');
  };

  const showOptimizationSuggestions = () => {
    console.log('Showing optimization suggestions...');
  };

  return (
    <DeveloperPageLayout
      title="Technical Cost Analysis"
      subtitle="Optimización y eficiencia de recursos"
      refreshInterval={30000}
      quickActions={[
        {
          icon: <Calculator className="w-4 h-4" />,
          label: "Cost Calculator",
          onClick: openCostCalculator
        },
        {
          icon: <TrendingDown className="w-4 h-4" />,
          label: "Optimization Tips",
          onClick: showOptimizationSuggestions
        }
      ]}
    >
      {/* Technical Cost Metrics */}
      <div className="grid grid-cols-5 gap-3">
        <TechnicalMetricCard
          title="Token Efficiency"
          value={`${costs.tokenEfficiency}%`}
          subtitle="Prompt/Completion ratio"
          status={costs.tokenEfficiency > 60 ? 'success' : 'warning'}
        />
        <TechnicalMetricCard
          title="API Call Cost"
          value={costs.avgApiCallCost}
          subtitle="Average per call"
          trend={costs.apiCallTrend}
        />
        <TechnicalMetricCard
          title="Cache Hit Rate"
          value={`${costs.cacheHitRate}%`}
          subtitle="Saved API calls"
          status="success"
        />
        <TechnicalMetricCard
          title="Wasted Tokens"
          value={formatNumber(costs.wastedTokens)}
          subtitle="From errors/retries"
          status="error"
        />
        <TechnicalMetricCard
          title="Cost per User"
          value={costs.costPerUser}
          subtitle="Monthly average"
        />
      </div>

      {/* Detailed Analysis Tabs */}
      <Tabs defaultValue="tokens" className="mt-6">
        <TabsList>
          <TabsTrigger value="tokens">Token Analysis</TabsTrigger>
          <TabsTrigger value="services">Service Breakdown</TabsTrigger>
          <TabsTrigger value="optimization">Optimization</TabsTrigger>
          <TabsTrigger value="forecasting">Forecasting</TabsTrigger>
        </TabsList>
        
        <TabsContent value="tokens">
          <TokenAnalysis />
        </TabsContent>
        
        <TabsContent value="services">
          <ServiceCostBreakdown />
        </TabsContent>
        
        <TabsContent value="optimization">
          <CostOptimization />
        </TabsContent>
        
        <TabsContent value="forecasting">
          <CostForecasting />
        </TabsContent>
      </Tabs>
    </DeveloperPageLayout>
  );
}